import { useAuth } from "@/contexts/AuthContext";
import { ItemMarginChip } from "@/components/ProfitBoard";
import { useEffect, useMemo, useState } from "react";
import { useDraft, clearDraft } from "@/lib/draft";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { api, formatApiError, brl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, Loader2, CheckCircle2, XCircle, Receipt, MessageCircle, FileX, Boxes, PackageOpen, Search } from "lucide-react";
import { SharePdfButton } from "@/components/SharePdfButton";
import { ProductPicker } from "@/components/erp/StockDialogs";
import { toast } from "sonner";
import { SignaturePad, ApprovalInfo } from "@/components/SignaturePad";
import { ExplainBudgetButton } from "@/components/ExplainBudget";
import { LaborPicker } from "@/components/Labor";
import { ServiceSuggestInput, SaveAsServiceButton } from "@/components/ServiceSuggest";
import { HelpTip } from "@/components/HelpTip";
import { KitList } from "@/components/ServiceKits";
import { useProductCosts, ItemProfitLine, BudgetProfitTotals } from "@/components/BudgetProfit";

const numLbl = "block text-[10px] font-semibold uppercase tracking-normal sm:tracking-wider text-zinc-700 dark:text-zinc-300 mb-1 whitespace-nowrap";
const actBtn = (on) => `h-11 rounded-lg border text-sm font-semibold inline-flex items-center justify-center gap-1.5 transition-colors ${on ? "border-green-brand bg-green-brand/10 text-green-300" : "border-zinc-800 bg-zinc-900 text-zinc-200 hover:bg-zinc-800"}`;

export function BudgetSection({ order, readOnly, onUpdated, whatsappLink, draftRef, onLiveItems, onLiveTotal }) {
  const budget = order.budget;
  const decision = budget?.decision;

  // Owner edit state
  const [editing, setEditing] = useState(!budget);
  const [items, setItems] = useState(
    budget?.items?.length ? budget.items : [{ description: "", quantity: 1, unit_price: 0, _new: true }],
  );
  useEffect(() => { onLiveItems?.(editing ? items : null); }, [items, editing]); // eslint-disable-line react-hooks/exhaustive-deps
  const [rejected, setRejected] = useState(budget?.rejected_items || []);
  const sameItem = (a, b) => ["description", "quantity", "unit_price", "product_id", "discount"].every((k) => String(a?.[k] ?? "") === String(b?.[k] ?? ""));
  const toggleServer = async (action, index, extra = {}) => {
    try {
      const { data } = await api.post(`/orders/${order.id}/budget/rejected`, { action, index, ...extra });
      onUpdated?.(data);
      return data;
    } catch (e) { toast.error(formatApiError(e)); return null; }
  };
  const [rejectAsk, setRejectAsk] = useState(null);
  const rejectRow = (i) => setRejectAsk(i);
  const doReject = async (i, reason, note, fdate) => {
    const it = { ...items[i], reject_reason: reason, reject_note: (reason === "Outro" || reason === "Vai fazer depois") ? (note || "").trim() || null : null, follow_up_date: reason === "Vai fazer depois" ? (fdate || null) : null, follow_up_note: reason === "Vai fazer depois" ? ((note || "").trim() || null) : null };
    const si = !items[i]._new && budget ? (budget.items || []).findIndex((b) => sameItem(b, items[i])) : -1;
    if (si >= 0 && !(await toggleServer("reject", si, { reason, note: it.reject_note }))) { return; }
    setRejected((r) => [...r, si >= 0 ? it : { ...it, _local: true }]);
    setItems((p) => { const n = p.filter((_, idx) => idx !== i); return n.length ? n : [{ description: "", quantity: 1, unit_price: 0, _new: true }]; });
  };
  const approveRow = async (i) => {
    const it = rejected[i];
    if (!it._local) {
      const si = rejected.slice(0, i).filter((x) => !x._local).length;
      if (!(await toggleServer("approve", si))) { return; }
    }
    const { _local, reject_reason, reject_note, ...clean } = it;
    setRejected((r) => r.filter((_, idx) => idx !== i));
    setItems((p) => [...p.filter((x) => x.description || Number(x.unit_price)), _local ? { ...clean, _new: true } : clean]);
    if (_local) { setEditing(true); }
  };
  const [notes, setNotes] = useState(budget?.notes || "");
  const [followUpDate, setFollowUpDate] = useState(budget?.follow_up_date || "");
  const [followUpNote, setFollowUpNote] = useState(budget?.follow_up_note || "");
  const { user: authUser } = useAuth();
  const [groupLaborPdf, setGroupLaborPdf] = useState(budget?.group_labor_pdf != null ? !!budget.group_labor_pdf : !!authUser?.shop?.group_labor_pdf_default);
  const [discKind, setDiscKind] = useState(budget?.total_discount?.kind || "percent");
  const [discInput, setDiscInput] = useState(() => {
    const v = Number(budget?.total_discount?.value || 0);
    return v > 0 ? (budget.total_discount.kind === "value" ? v.toFixed(2).replace(".", ",") : String(v)) : "";
  });
  const [saving, setSaving] = useState(false);
  const [deciding, setDeciding] = useState(false);
  const [signOpen, setSignOpen] = useState(false);
  const [pickRow, setPickRow] = useState(null);
  const [panel, setPanel] = useState(null);
  const costMap = useProductCosts();
  const subtotal = items.reduce((s, i) => s + (Number(i.quantity) || 0) * (Number(i.unit_price) || 0) - (Number(i.discount) || 0), 0);
  const itemsGross = items.reduce((s, i) => s + (Number(i.quantity) || 0) * (Number(i.unit_price) || 0), 0);
  const itemsDiscount = items.reduce((s, i) => s + (Number(i.discount) || 0), 0);
  const round2 = (n) => {
    const sign = n < 0 ? -1 : 1;
    return sign * Math.round(Math.abs(n) * 100 + 1e-9) / 100;
  };
  const discountVal = (() => {
    const n = Number((discInput || "0").toString().replace(",", ".")) || 0;
    if (!n) return 0;
    if (discKind === "percent") return round2(subtotal * Math.min(Math.max(n, 0), 100) / 100);
    return round2(Math.min(Math.max(n, 0), subtotal));
  })();
  const total = Math.max(0, round2(subtotal - discountVal));
  useEffect(() => { onLiveTotal?.(editing ? total : null); }, [total, editing]); // eslint-disable-line react-hooks/exhaustive-deps
  const draftValue = useMemo(() => ({ items, notes }), [items, notes]);
  useDraft({ orderId: order.id, kind: "budget", value: { items, notes, discKind, discInput, rejected }, enabled: !readOnly, saveEnabled: editing, onRestore: (d) => {
    if (!d?.items?.length) return;
    setItems(d.items); setNotes(d.notes || "");
    if (d.rejected) setRejected(d.rejected);
    if (d.discKind) setDiscKind(d.discKind);
    if (d.discInput != null) setDiscInput(d.discInput);
    setEditing(true);
  } });

  const needKind = (list) => list.some((i) => i._new && !i.product_id && !i.customer_supplied && !i.kind);
  const addRow = () => setItems((p) => [...p, { description: "", quantity: 1, unit_price: 0, _new: true }]);
  const rmRow = (i) => setItems((p) => p.filter((_, idx) => idx !== i));
  const setRow = (i, k, v) => setItems((p) => p.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const pickProduct = (p) => {
    setItems((prev) => prev.map((it, idx) => (idx === pickRow ? { ...it, description: p.description, unit_price: p.price, product_id: p.id, kind: "p", cost: null } : it)));
    if (p.stock <= 0) toast.warning("Peça sem estoque: o saldo vai ficar negativo ao aprovar");
  };

  const normItem = ({ _new, ...i }) => ({ ...i, kind: i.kind || (i.product_id || i.customer_supplied ? "p" : "s"), quantity: Number(i.quantity) || 0, unit_price: Number(i.unit_price) || 0, discount: Number(i.discount) || 0, cost: i.cost === "" || i.cost == null ? null : Number(i.cost), due_months: i.due_months ? Number(i.due_months) : null, due_km: i.due_km ? Number(i.due_km) : null, follow_up_date: i.follow_up_date || null, follow_up_note: i.follow_up_note || null });
  const toPayload = (list, rej = rejected) => ({
    items: list.map(normItem),
    rejected_items: rej.map(({ _local, ...x }) => normItem(x)),
    notes,
    total_discount: Number((discInput || "0").toString().replace(",", ".")) > 0 ? { kind: discKind, value: Number((discInput || "0").toString().replace(",", ".")) } : null,
    follow_up_date: followUpDate || null,
    follow_up_note: (followUpNote || "").trim() || null,
    group_labor_pdf: groupLaborPdf,
  });
  if (draftRef) {
    draftRef.current = async () => {
      if (readOnly || !editing) return true;
      const list = items.filter((i) => (i.description || "").trim() || Number(i.unit_price));
      if (!list.length) return true;
      if (list.some((i) => !(i.description || "").trim())) { toast.error("Orçamento: preencha a descrição de todos os itens"); return false; }
      const p = toPayload(list);
      const same = budget && JSON.stringify(p) === JSON.stringify(toPayload(budget.items || [], budget.rejected_items || [])) && (budget.notes || "") === (notes || "");
      if (same) return true;
      if (needKind(list)) { toast.error("Orçamento: escolha Peça ou Serviço em cada item digitado"); return false; }
      try {
        const { data } = await api.put(`/orders/${order.id}/budget`, { ...p, silent: true });
        clearDraft(order.id, "budget");
        setItems(data.budget?.items?.length ? data.budget.items : list);
        setEditing(false);
        onUpdated?.(data);
        return "saved";
      } catch (e) { toast.error(formatApiError(e)); return false; }
    };
  }

  const [autoSaving, setAutoSaving] = useState(false);
  const [autoSavedAt, setAutoSavedAt] = useState(null);
  useEffect(() => {
    if (readOnly || !editing) { return; }
    const list = items.filter((i) => (i.description || "").trim() || Number(i.unit_price));
    if (!list.length || list.some((i) => !(i.description || "").trim()) || needKind(list)) { return; }
    const p = toPayload(list);
    const same = budget && JSON.stringify(p) === JSON.stringify(toPayload(budget.items || [], budget.rejected_items || [])) && (budget.notes || "") === (notes || "");
    if (same) { return; }
    const t = setTimeout(async () => {
      try {
        setAutoSaving(true);
        const { data } = await api.put(`/orders/${order.id}/budget`, { ...p, silent: true });
        setAutoSavedAt(Date.now());
        onUpdated?.(data);
      } catch { /* silencioso: segue como rascunho local */ }
      finally { setAutoSaving(false); }
    }, 2500);
    return () => clearTimeout(t);
  }, [items, notes, discKind, discInput, rejected, editing, readOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (items.length === 0 || items.some((i) => !i.description)) {
      toast.error("Preencha a descrição de todos os itens");
      return;
    }
    if (needKind(items)) { toast.error("Escolha Peça ou Serviço em cada item digitado"); return; }
    setSaving(true);
    try {
      const payload = toPayload(items);
      const { data } = await api.put(`/orders/${order.id}/budget`, payload);
      clearDraft(order.id, "budget");
      toast.success("Orçamento enviado ao cliente");
      setEditing(false);
      onUpdated?.(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const decide = async (d, signature) => {
    setDeciding(true);
    try {
      let reason;
      if (d === "recusado") {
        reason = window.prompt("Quer contar o motivo da recusa? (opcional)", "");
        if (reason === null) { setDeciding(false); return; }
      }
      const { data } = await api.post(`/orders/${order.id}/budget/decision`, { decision: d, signature, reason: reason || null });
      toast.success(d === "aprovado" ? "Orçamento aprovado" : "Orçamento recusado");
      setSignOpen(false);
      onUpdated?.(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeciding(false);
    }
  };

  // --- Customer read-only view
  if (readOnly) {
    if (!budget) {
      return (
        <div data-testid="budget-empty" className="rounded-xl border border-dashed border-zinc-800 bg-zinc-950/40 p-6 text-center text-sm text-zinc-500">
          A oficina ainda não enviou um orçamento.
        </div>
      );
    }
    return (
      <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-5" data-testid="budget-view">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Receipt className="h-4 w-4 text-green-brand" />
            <h3 className="font-display text-lg font-bold uppercase text-zinc-100">Orçamento</h3>{!readOnly && <HelpTip screen="orcamento" className="h-7 w-7" />}
          </div>
          <DecisionBadge decision={decision} />
        </div>
        <BudgetTable items={budget.items} total={budget.total} subtotal={budget.subtotal} totalDiscount={budget.total_discount_amount} />
        <RejectedItems list={budget.rejected_items || []} />
        {budget.notes && (
          <div className="mt-3 text-sm text-zinc-400">
            <div className="text-[11px] font-mono-caps text-zinc-500 mb-1">Observações</div>
            {budget.notes}
          </div>
        )}
        {decision === "pendente" && (
          <div className="mt-5 grid grid-cols-2 gap-3">
            <Button
              data-testid="budget-reject-btn"
              variant="outline"
              onClick={() => decide("recusado")}
              disabled={deciding}
              className="h-11 bg-zinc-900 border-red-900 text-red-300 hover:bg-red-950/40 hover:text-red-200 font-bold uppercase"
            >
              {deciding ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><XCircle className="h-4 w-4 mr-2" /> Recusar</>)}
            </Button>
            <Button
              data-testid="budget-approve-btn"
              onClick={() => setSignOpen(true)}
              disabled={deciding}
              className="h-11 bg-green-brand hover:bg-green-brandDark text-white font-bold uppercase"
            >
              {deciding ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><CheckCircle2 className="h-4 w-4 mr-2" /> Aprovar</>)}
            </Button>
          </div>
        )}
        {decision === "aprovado" && <ApprovalInfo orderId={order.id} approval={budget.approval} />}
        <SignaturePad
          open={signOpen}
          onOpenChange={setSignOpen}
          busy={deciding}
          customerName={order.customer_name}
          onConfirm={(sig) => decide("aprovado", sig)}
        />
      </div>
    );
  }

  // --- Owner editable view
  if (!editing && budget) {
    const reopen = async () => {
      try { const { data } = await api.post(`/orders/${order.id}/reopen`); onUpdated?.(data); setEditing(true); toast.success("OS reaberta — edite o orçamento e envie de novo"); }
      catch (e) { toast.error(formatApiError(e)); }
    };
    const waHref = typeof whatsappLink === "function" ? whatsappLink() : "";
    const canWhatsapp = !!order?.customer_phone && !!waHref && decision === "pendente";
    return (
      <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-5" data-testid="budget-owner-view">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Receipt className="h-4 w-4 text-green-brand" />
            <h3 className="font-display text-lg font-bold uppercase text-zinc-100">Orçamento</h3>{!readOnly && <HelpTip screen="orcamento" className="h-7 w-7" />}
          </div>
          <DecisionBadge decision={decision} />
        </div>
        <BudgetTable items={budget.items} total={budget.total} subtotal={budget.subtotal} totalDiscount={budget.total_discount_amount} showMargin={!readOnly} />
        <RejectedItems list={rejected} onApprove={approveRow} />
        <PreviousRejected order={order} current={items} onAdd={(it) => { setItems((p) => [...p, it]); setEditing(true); }} />
        {budget.notes && <div className="mt-3 text-sm text-zinc-400"><div className="text-[11px] font-mono-caps text-zinc-500 mb-1">Observações</div>{budget.notes}</div>}
        {decision === "aprovado" && <ApprovalInfo orderId={order.id} approval={budget.approval} />}
        {order.status === "nao_realizado" && (
          <div data-testid="budget-nao-realizado-info" className="mt-4 rounded-xl border border-red-900 bg-red-950/30 p-4 text-sm text-red-200">
            OS encerrada como <strong>Não realizado</strong>.
            {order.assessment_fee != null && (
              <span> Taxa de avaliação: <strong data-testid="assessment-fee-value">{brl(order.assessment_fee)}</strong></span>
            )}
          </div>
        )}
        {decision === "recusado" && !readOnly && order.status !== "nao_realizado" && (
          <div data-testid="budget-recusado-banner" className="mt-4 rounded-xl border border-red-800 bg-red-950/40 p-4">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-red-700 bg-red-900/60 text-red-200 text-xs font-bold uppercase tracking-wide">Recusado</span>
              <span className="text-xs text-red-200/80">O cliente recusou o orçamento{budget.decided_at ? ` em ${new Date(budget.decided_at).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}` : ""}.</span>
            </div>
            {budget.refused_reason && <p data-testid="budget-refused-reason" className="text-sm text-red-100 mb-2"><b>Motivo:</b> {budget.refused_reason}</p>}
            <div className="flex flex-wrap items-center gap-2">
              {order.customer_phone && whatsappLink && (
                <a data-testid="wa-recusado-btn" href={whatsappLink()} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 h-10 px-3 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold uppercase">
                  <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                </a>
              )}
              <Button data-testid="budget-reopen-btn" type="button" onClick={reopen} className="h-10 bg-amber-brand hover:bg-amber-brandDark text-black font-bold uppercase">
                Reabrir (voltar para Aprovação)
              </Button>
              <a data-testid="budget-funnel-link" href="/owner/funil" className="inline-flex items-center h-10 px-3 rounded-md border border-zinc-700 text-zinc-200 text-xs font-bold uppercase hover:bg-zinc-900">Recuperar no Funil</a>
              <CloseUnrealizedButton order={order} onUpdated={onUpdated} />
            </div>
          </div>
        )}
        <BudgetHistory order={order} />
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button
            data-testid="budget-edit-btn"
            variant="outline"
            onClick={() => setEditing(true)}
            className="bg-zinc-900 border-zinc-800 text-zinc-200 hover:bg-zinc-800 hover:text-white"
          >
            Editar orçamento
          </Button>
          <ExplainBudgetButton order={order} onSaved={onUpdated} />
          {canWhatsapp && (
            <a
              data-testid="wa-budget-btn"
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              title="Abrir WhatsApp com orçamento pronto"
              className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-bold uppercase tracking-wide"
            >
              <MessageCircle className="h-4 w-4" /> Avisar no WhatsApp
            </a>
          )}
          <SharePdfButton order={order} />
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-5" data-testid="budget-editor">
      <div className="flex items-center gap-2 mb-3">
        <Receipt className="h-4 w-4 text-green-brand" />
        <h3 className="font-display text-lg font-bold uppercase text-zinc-100">
          {budget ? "Editar orçamento" : "Novo orçamento"}
        </h3>
        {editing && !readOnly && (autoSaving || autoSavedAt) && (
          <span data-testid="budget-autosave" className="ml-auto text-[11px] font-mono-caps uppercase text-zinc-500">
            {autoSaving ? "Salvando rascunho…" : "Rascunho salvo"}
          </span>
        )}
      </div>
      {budget?.approval && (
        <div data-testid="budget-reapproval-warning" className="mb-3 rounded-lg border border-amber-brand/40 bg-amber-brand/10 p-3 text-xs text-amber-200">
          Este orçamento já foi aprovado e assinado. Ao enviar a alteração, a assinatura atual vai para o histórico e o cliente precisará aprovar e assinar de novo.
        </div>
      )}
      <div className="space-y-3">
        {items.map((it, i) => (
          <div key={i} data-testid={`budget-row-${i}`} className="rounded-xl border-2 border-zinc-400 dark:border-zinc-700 bg-white dark:bg-zinc-900/40 p-3 space-y-2.5 shadow-sm">
            <div className="flex flex-wrap sm:flex-nowrap items-start gap-2">
              <label className="shrink-0 w-24"><span className={numLbl}>Cód.</span>
                <Input data-testid={`budget-row-${i}-code`} value={it.code || ""} onChange={(e) => setRow(i, "code", e.target.value)} className="h-10 bg-zinc-900 border-zinc-800 px-2 text-xs font-mono" placeholder="opcional" /></label>
              <div className="order-first sm:order-none basis-full sm:basis-auto flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2"><span className={numLbl}>Descrição</span>
                <div className="flex items-center gap-1.5">
                  <button type="button" data-testid={`budget-row-${i}-reject`} onClick={() => rejectRow(i)} className="inline-flex items-center rounded-md border border-red-800 px-2 py-0.5 text-[11px] font-bold text-red-300 hover:bg-red-950/40">Não aprovado</button>
                  {!it.product_id && !it.customer_supplied && (() => { const ek = it.kind || (it._new ? null : "s"); return <span data-testid={`budget-row-${i}-kind`} className={`inline-flex items-center rounded-md border overflow-hidden text-[11px] font-bold ${ek ? "border-zinc-700" : "border-red-700 animate-pulse"}`}>
                    {!ek && <span className="px-1.5 text-red-300">Escolha:</span>}
                    {[["p", "Peça"], ["s", "Serviço"]].map(([k, l]) => <button key={k} type="button" data-testid={`budget-row-${i}-kind-${k}`} onClick={() => setRow(i, "kind", k)} className={`px-2 py-0.5 ${ek === k ? (k === "p" ? "bg-sky-900/60 text-sky-200" : "bg-amber-brand/25 text-amber-200") : "text-zinc-400 hover:text-zinc-200"}`}>{l}</button>)}</span>; })()}
                  {it.product_id && <span className="text-[10px] font-bold uppercase text-sky-300">Peça do estoque</span>}</div></div>
                <div className="relative">
                  <ServiceSuggestInput testid={`budget-row-${i}-desc`} value={it.description} onChange={(v) => setRow(i, "description", v)}
                    onPick={(s) => setItems((p) => p.map((row, idx) => (idx === i ? { ...row, ...s, product_id: null, customer_supplied: false, kind: "s" } : row)))}
                    className={`h-10 bg-zinc-900 border-zinc-800 ${it.hours ? "pr-14" : ""}`} placeholder="Troca de óleo + filtro" />
                  {!!it.hours && <span data-testid={`budget-row-${i}-hours`} title="Tempo padrão (só o dono vê)" className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-bold text-amber-300 bg-amber-950/60 rounded px-1">{String(it.hours).replace(".", ",")}h</span>}
                </div>
              </div>
              <div className="flex gap-1 pt-[18px] shrink-0">
                <button type="button" data-testid={`budget-row-${i}-stock`} onClick={() => setPickRow(i)} disabled={!!it.customer_supplied} title={it.customer_supplied ? "Peça fornecida pelo cliente — não sai do estoque" : it.product_id ? "Peça do estoque" : "Buscar peça no estoque"} aria-label="Buscar peça no estoque"
                  className={`h-10 w-10 flex items-center justify-center rounded-md border transition-colors ${it.customer_supplied ? "border-zinc-800 text-zinc-700 opacity-50 cursor-not-allowed" : it.product_id ? "border-green-brand text-green-neon bg-green-brand/10" : "border-zinc-800 text-zinc-400 hover:text-green-neon"}`}><Boxes className="h-4 w-4" /></button>
                <button type="button" data-testid={`budget-row-${i}-remove`} onClick={() => rmRow(i)} aria-label="Remover item"
                  className="h-10 w-10 flex items-center justify-center rounded-md border border-zinc-800 text-red-400 hover:text-red-300 hover:border-red-900 transition-colors"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
            <label className="flex items-center gap-2 text-[11px] text-zinc-400"><input type="checkbox" data-testid={`budget-row-${i}-customer-supplied`} checked={!!it.customer_supplied} onChange={(e) => setItems((p) => p.map((row, idx) => idx === i ? { ...row, customer_supplied: e.target.checked, product_id: e.target.checked ? null : row.product_id, unit_price: e.target.checked ? 0 : row.unit_price, discount: e.target.checked ? 0 : row.discount } : row))} className="h-3.5 w-3.5 accent-amber-brand" /> Peça fornecida pelo cliente <span className="text-zinc-600">(valor 0,00 e não sai do estoque)</span>
              {!it.product_id && !it.customer_supplied && <SaveAsServiceButton item={it} testid={`budget-row-${i}-save-service`} />}</label>
            <div className={`grid ${!it.customer_supplied ? "grid-cols-2 sm:grid-cols-[6rem_11rem_11rem_11rem]" : "grid-cols-[3.75rem_1fr_1fr] sm:grid-cols-[6rem_11rem_11rem]"} gap-2`}>
              <label className="min-w-0"><span className={numLbl}>Qtd</span>
                <Input data-testid={`budget-row-${i}-qty`} type="number" step="0.1" inputMode="decimal" value={it.quantity} onChange={(e) => setRow(i, "quantity", e.target.value)} className="h-10 bg-zinc-900 border-zinc-800 px-2 text-center" aria-label="Quantidade" /></label>
              <label className="min-w-0"><span className={numLbl}>Valor unit. (R$)</span>
                <Input data-testid={`budget-row-${i}-price`} type="number" step="0.01" inputMode="decimal" value={it.unit_price} onChange={(e) => setRow(i, "unit_price", e.target.value)} className="h-10 bg-zinc-900 border-zinc-800 px-2" aria-label="Valor unitário" /></label>
              <label className="min-w-0"><span className={numLbl}>Desconto (R$)</span>
                <Input data-testid={`budget-row-${i}-discount`} type="number" step="0.01" min="0" inputMode="decimal" value={it.discount ?? ""} onChange={(e) => setRow(i, "discount", e.target.value)} className="h-10 bg-zinc-900 border-zinc-800 px-2" aria-label="Desconto do item (R$)" /></label>
              {!it.customer_supplied && <label className="min-w-0" title="Só o dono vê — nunca vai para o PDF nem para o cliente"><span className={`${numLbl} text-amber-700 dark:text-amber-300/90`}>Custo (R$)</span>
                <Input data-testid={`budget-row-${i}-cost`} type="number" step="0.01" min="0" inputMode="decimal" value={it.cost ?? (it.product_id && costMap[it.product_id] != null ? costMap[it.product_id] : "")} onChange={(e) => setRow(i, "cost", e.target.value)} placeholder="informe" className={`h-10 px-2 ${it.cost == null && !(it.product_id && costMap[it.product_id] != null) ? "bg-red-50 dark:bg-red-950/20 border-2 border-red-500 dark:border-red-500 text-red-700 dark:text-red-200 placeholder:text-red-600 dark:placeholder:text-red-300 placeholder:font-bold" : "bg-amber-50 dark:bg-amber-950/20 border-amber-500 dark:border-amber-900/50 text-zinc-900 dark:text-zinc-100"}`} aria-label="Custo do item (só o dono vê)" /></label>}
            </div>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 text-[11px] text-zinc-600 dark:text-zinc-400" data-testid={`budget-row-${i}-due`}>
              <span className="font-mono-caps shrink-0 w-full sm:w-auto text-zinc-700 dark:text-zinc-300">Vence em <span className="normal-case tracking-normal text-zinc-500 dark:text-zinc-500">(opcional, vira lembrete)</span></span>
              <Input data-testid={`budget-row-${i}-due-months`} type="text" inputMode="numeric" value={it.due_months ?? ""} onChange={(e) => setRow(i, "due_months", e.target.value.replace(/\D/g, ""))} placeholder="meses" className="h-8 w-[4.5rem] bg-zinc-900 border-zinc-800 text-xs" aria-label="Vence em meses" />
              <span className="shrink-0">e/ou</span>
              <Input data-testid={`budget-row-${i}-due-km`} type="text" inputMode="numeric" value={it.due_km ?? ""} onChange={(e) => setRow(i, "due_km", e.target.value.replace(/\D/g, ""))} placeholder="km" className="h-8 w-[5.5rem] bg-zinc-900 border-zinc-800 text-xs" aria-label="Vence em km" />
              <span className="ml-auto text-xs font-bold text-zinc-200 tabular-nums" data-testid={`budget-row-${i}-subtotal`}><span className="font-normal text-zinc-500 mr-1">Item</span>{brl((Number(it.quantity) || 0) * (Number(it.unit_price) || 0) - (Number(it.discount) || 0))}</span>
            </div>
            <ItemProfitLine testid={`budget-row-${i}-profit`} item={it} costMap={costMap} />
          </div>
        ))}
      </div>
      <RejectedItems list={rejected} onApprove={approveRow} />
      <PreviousRejected order={order} current={items} onAdd={(it) => setItems((p) => [...p.filter((x) => x.description || Number(x.unit_price)), it])} />
      <RejectReasonDialog open={rejectAsk !== null} onClose={() => setRejectAsk(null)} onConfirm={(r, n, fd) => { const i = rejectAsk; setRejectAsk(null); doReject(i, r, n, fd); }} />
      <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="budget-actions">
        <button type="button" data-testid="budget-add-row" onClick={addRow} className={actBtn(false)}><Plus className="h-4 w-4" /> Adicionar item</button>
        <button type="button" data-testid="budget-apply-kit" onClick={() => setPanel(panel === "kit" ? null : "kit")} className={actBtn(panel === "kit")}><PackageOpen className="h-4 w-4" /> Aplicar kit</button>
        <button type="button" data-testid="budget-open-search" onClick={() => setPanel(panel === "search" ? null : "search")} className={`${actBtn(panel === "search")} col-span-2 sm:col-span-1 sm:order-first`}><Search className="h-4 w-4" /> Buscar peça ou serviço</button>
      </div>
      {panel === "search" && <LaborPicker inline autoFocus onPick={(it) => { setItems((p) => [...p.filter((x) => x.description || Number(x.unit_price)), { ...it, kind: it.product_id ? "p" : "s" }]); setPanel(null); }} />}
      {panel === "kit" && <KitList onPick={(kitItems) => { setItems((prev) => {
        const base = prev.filter((x) => x.description || Number(x.unit_price));
        return [...base, ...kitItems.map((i) => ({ description: i.description, quantity: Number(i.quantity) || 1, unit_price: Number(i.unit_price) || 0, discount: Number(i.discount) || 0, product_id: i.product_id || null, kind: i.product_id ? "p" : "s" }))];
      }); setPanel(null); }} />}
      <p className="mt-2 text-[11px] leading-snug text-zinc-600 dark:text-zinc-400">Ícone de caixa: puxa a peça do estoque (sai do estoque quando o cliente aprovar). Serviços são livres. Kits são cadastrados em Configurações › Oficina.</p>
      <ProductPicker open={pickRow !== null} onOpenChange={(o) => !o && setPickRow(null)} onPick={pickProduct} />
      <div className="mt-4">
        <div className={numLbl}>Observações</div>
        <Textarea data-testid="budget-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className="bg-zinc-900 border-zinc-800 text-zinc-100 min-h-[70px]" placeholder="Garantia de 90 dias nas peças..." />
      </div>
      <div data-testid="budget-totals" className="mt-4 rounded-xl border border-zinc-800 bg-zinc-900/40 p-3 space-y-2 text-sm">
        <div className="flex justify-between text-zinc-400"><span>Subtotal dos itens</span><span className="tabular-nums" data-testid="budget-subtotal">{brl(subtotal)}</span></div>
        <div className="rounded-lg border border-amber-brand/30 bg-amber-brand/5 p-3 space-y-2" data-testid="budget-total-discount">
          <div className="text-[11px] font-mono-caps text-amber-300 uppercase">Desconto total do orçamento</div>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-md overflow-hidden border border-zinc-800">
              <button type="button" data-testid="budget-td-kind-percent" onClick={() => setDiscKind("percent")} className={`px-3 h-9 text-xs font-bold ${discKind === "percent" ? "bg-amber-brand text-zinc-950" : "bg-zinc-900 text-zinc-300"}`}>%</button>
              <button type="button" data-testid="budget-td-kind-value" onClick={() => setDiscKind("value")} className={`px-3 h-9 text-xs font-bold ${discKind === "value" ? "bg-amber-brand text-zinc-950" : "bg-zinc-900 text-zinc-300"}`}>R$</button>
            </div>
            <Input data-testid="budget-td-value" value={discInput} onChange={(e) => setDiscInput(e.target.value.replace(/[^0-9.,]/g, ""))} placeholder={discKind === "percent" ? "ex.: 5" : "ex.: 50,00"} className="h-9 bg-zinc-950 border-zinc-800 flex-1" />
            {discountVal > 0 && <span className="text-xs text-amber-200 tabular-nums whitespace-nowrap" data-testid="budget-td-amount">− {brl(discountVal)}</span>}
          </div>
          <p className="text-[10px] text-zinc-500">Aplicado por cima do subtotal. Os descontos por item ficam como estão.</p>
        </div>
        {itemsDiscount > 0 && <div className="flex justify-between text-zinc-500 text-[11px]"><span>Descontos por item já incluídos</span><span className="tabular-nums">− {brl(itemsDiscount)}</span></div>}
        <div className="flex justify-between items-baseline pt-2 border-t border-zinc-800"><span className="font-mono-caps text-[11px] text-zinc-400">Total final</span><span data-testid="budget-total" className="font-display text-2xl font-black text-amber-brand">{brl(total)}</span></div>
      </div>
      <div data-testid="budget-followup" className="mt-3 rounded-xl border border-amber-brand/30 bg-amber-brand/5 p-3 space-y-2">
        <div className="text-[11px] font-mono-caps uppercase text-amber-300">Retornar em (opcional)</div>
        <div className="grid grid-cols-1 sm:grid-cols-[11rem_1fr] gap-2">
          <Input data-testid="budget-followup-date" type="date" value={followUpDate} onChange={(e) => setFollowUpDate(e.target.value)} className="h-10 bg-zinc-900 border-zinc-800" />
          <Input data-testid="budget-followup-note" value={followUpNote} onChange={(e) => setFollowUpNote(e.target.value)} placeholder="Ex.: cartão vira mês que vem" className="h-10 bg-zinc-900 border-zinc-800" />
        </div>
        <p className="text-[10px] text-zinc-500">Na data, o cliente aparece em Lembretes como “Retornar orçamento”.</p>
      </div>
      <label data-testid="budget-group-labor" className="mt-3 flex items-start sm:items-center gap-3 rounded-xl border border-zinc-800 bg-zinc-900/40 p-3 cursor-pointer select-none flex-wrap">
        <input data-testid="budget-group-labor-input" type="checkbox" checked={groupLaborPdf} onChange={(e) => setGroupLaborPdf(e.target.checked)} className="h-5 w-5 accent-green-brand mt-0.5 sm:mt-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-zinc-100">Agrupar mão de obra no PDF</div>
          <p className="text-[11px] text-zinc-500">No PDF do orçamento/OS enviado ao cliente, os serviços aparecem sem valor individual e somam uma só linha “Mão de obra dos itens acima”. Peças, totais, rentabilidade e comissões não mudam.</p>
        </div>
      </label>
      <BudgetProfitTotals testid="budget-profit-totals" items={items} costMap={costMap} />
      <div className="mt-3">
        <div className="flex flex-wrap items-stretch gap-2 sm:justify-end">
          {budget && (
            <Button
              type="button"
              variant="outline"
              onClick={() => { clearDraft(order.id, "budget"); setItems(budget.items?.length ? budget.items : [{ description: "", quantity: 1, unit_price: 0 }]); setRejected(budget.rejected_items || []); setNotes(budget.notes || ""); setEditing(false); }}
              data-testid="budget-cancel-edit"
              className="flex-1 min-w-[9rem] bg-zinc-900 border-zinc-800 text-zinc-200 hover:bg-zinc-800"
            >
              Cancelar
            </Button>
          )}
          <Button
            data-testid="budget-save-btn"
            onClick={save}
            disabled={saving}
            className="flex-1 min-w-[11rem] bg-green-brand hover:bg-green-brandDark text-white font-bold uppercase"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enviar ao cliente"}
          </Button>
        </div>
      </div>
    </div>
  );
}

const REJECT_REASONS = ["Preço", "Vai fazer depois", "Vai fazer em outro lugar", "Outro"];

function RejectReasonDialog({ open, onClose, onConfirm }) {
  const [r, setR] = useState("");
  const [note, setNote] = useState("");
  const [fdate, setFdate] = useState("");
  useEffect(() => { if (open) { setR(""); setNote(""); setFdate(""); } }, [open]);
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { onClose(); } }}>
      <DialogContent data-testid="reject-reason-dialog" className="bg-zinc-950 border-zinc-800">
        <DialogHeader><DialogTitle>Motivo da recusa</DialogTitle><DialogDescription>Por que o cliente não aprovou este item?</DialogDescription></DialogHeader>
        <div className="space-y-2">
          {REJECT_REASONS.map((x, k) => (
            <label key={x} className="flex items-center gap-2 text-sm text-zinc-200"><input type="radio" name="reject-reason" data-testid={`reject-reason-${k}`} checked={r === x} onChange={() => setR(x)} className="accent-amber-brand" /> {x}</label>
          ))}
          {r === "Outro" && <Input data-testid="reject-reason-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Descreva o motivo" className="bg-zinc-900 border-zinc-800" />}
          {r === "Vai fazer depois" && (
            <div className="rounded-md border border-amber-brand/30 bg-amber-brand/5 p-2 space-y-1">
              <div className="text-[10px] font-mono-caps uppercase text-amber-300">Retornar em (opcional)</div>
              <Input data-testid="reject-reason-followup-date" type="date" value={fdate} onChange={(e) => setFdate(e.target.value)} className="bg-zinc-900 border-zinc-800" />
              <Input data-testid="reject-reason-followup-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ex.: cartão vira mês que vem" className="bg-zinc-900 border-zinc-800" />
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="outline" data-testid="reject-reason-cancel" onClick={onClose} className="bg-zinc-900 border-zinc-800">Cancelar</Button>
          <Button data-testid="reject-reason-confirm" disabled={!r || (r === "Outro" && !note.trim())} onClick={() => onConfirm(r, note, fdate)} className="bg-red-700 hover:bg-red-600 text-white font-bold">Marcar como não aprovado</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PreviousRejected({ order, current, onAdd }) {
  const [list, setList] = useState([]);
  useEffect(() => { api.get(`/orders/${order.id}/previous-rejected`).then(({ data }) => setList(data || [])).catch(() => setList([])); }, [order.id]);
  const have = new Set((current || []).map((x) => (x.description || "").trim().toLowerCase()));
  const rows = list.filter((r) => !have.has((r.item.description || "").trim().toLowerCase()));
  if (!rows.length) return null;
  return (
    <div data-testid="budget-previous-rejected" className="mt-4 rounded-xl border border-amber-brand/40 bg-amber-brand/5 p-3">
      <div className="text-[11px] font-mono-caps uppercase text-amber-300 mb-2">Recusados em visitas anteriores <span className="normal-case text-zinc-500">(ofereça de novo)</span></div>
      <ul className="divide-y divide-zinc-800">
        {rows.map((r, i) => (
          <li key={`${r.order_id}-${i}`} data-testid={`prev-rejected-${i}`} className="flex items-center justify-between gap-2 py-2 text-sm">
            <span className="min-w-0 text-zinc-300"><span className="block truncate">{r.item.description} <span className="text-xs text-zinc-500">· {brl((Number(r.item.quantity) || 0) * (Number(r.item.unit_price) || 0) - (Number(r.item.discount) || 0))}</span></span>
              <span className="block text-[11px] text-zinc-500">OS {r.order_number != null ? `#${r.order_number} · ` : ""}{r.date ? r.date.split("-").reverse().join("/") : "—"} · Motivo: {r.item.reject_reason || "não informado"}{r.item.reject_note ? `: ${r.item.reject_note}` : ""}</span></span>
            <button type="button" data-testid={`prev-rejected-${i}-add`} onClick={() => { const { reject_reason, reject_note, ...it } = r.item; onAdd({ ...it, _new: true, kind: it.kind || (it.product_id ? "p" : "s") }); toast.success("Item adicionado ao orçamento"); }}
              className="shrink-0 h-8 px-2 rounded-md border border-amber-brand/60 text-xs font-bold text-amber-200 hover:bg-amber-brand/10">Adicionar ao orçamento</button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function RejectedItems({ list, onApprove }) {
  if (!list?.length) return null;
  return (
    <div data-testid="budget-rejected" className="mt-4 rounded-xl border border-red-900/60 bg-red-950/20 p-3">
      <div className="text-[11px] font-mono-caps uppercase text-red-300 mb-2">Itens não aprovados <span className="normal-case text-zinc-500">(não entram no total)</span></div>
      <ul className="divide-y divide-zinc-800">
        {list.map((it, i) => (
          <li key={i} data-testid={`budget-rejected-${i}`} className="flex items-center justify-between gap-2 py-2 text-sm">
            <span className="min-w-0 text-zinc-300"><span className="block truncate">{it.description} <span className="text-xs text-zinc-500">· {Number(it.quantity) || 0} × {brl(Number(it.unit_price) || 0)}</span></span>
              <span data-testid={`budget-rejected-${i}-reason`} className="block text-[11px] text-red-300/90">Motivo: {it.reject_reason || "não informado"}{it.reject_note ? `: ${it.reject_note}` : ""}</span></span>
            <span className="flex items-center gap-2 shrink-0">
              <span className="tabular-nums text-zinc-400">{brl((Number(it.quantity) || 0) * (Number(it.unit_price) || 0) - (Number(it.discount) || 0))}</span>
              {onApprove && <button type="button" data-testid={`budget-rejected-${i}-approve`} onClick={() => onApprove(i)} className="h-8 px-2 rounded-md border border-green-brand/60 text-xs font-bold text-green-300 hover:bg-green-brand/10">Aprovar</button>}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CloseUnrealizedButton({ order, onUpdated }) {
  const [open, setOpen] = useState(false);
  const [fee, setFee] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = fee === "" ? {} : { assessment_fee: Number(fee.replace(/\./g, "").replace(",", ".")) };
      const { data } = await api.post(`/orders/${order.id}/close-unrealized`, payload);
      toast.success("OS encerrada como Não realizada");
      onUpdated?.(data);
      setOpen(false);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <>
      <Button data-testid="budget-close-unrealized-btn" type="button" variant="outline" onClick={() => setOpen(true)} className="h-auto min-h-10 whitespace-normal max-w-full bg-zinc-900 border-red-900 text-red-300 hover:bg-red-950/60">
        <FileX className="h-4 w-4 mr-2" /> Encerrar como Não realizado
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="close-unrealized-dialog" className="bg-zinc-950 border border-zinc-800 text-zinc-100 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display uppercase flex items-center gap-2"><FileX className="h-5 w-5 text-red-400" /> Encerrar como Não realizado</DialogTitle>
            <DialogDescription className="text-zinc-400">A OS fica encerrada. Se cobrou pela avaliação, informe o valor (opcional).</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3" data-testid="close-unrealized-form">
            <div>
              <Label className="text-xs font-mono-caps text-zinc-400">Valor cobrado pela avaliação (R$)</Label>
              <Input data-testid="close-unrealized-fee" type="text" value={fee} onChange={(e) => setFee(e.target.value.replace(/[^0-9.,]/g, ""))} placeholder="ex.: 50,00" className="mt-1 bg-zinc-900 border-zinc-800" />
            </div>
            <div className="flex items-center justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setOpen(false)} disabled={busy} className="text-zinc-300 hover:bg-zinc-900">Cancelar</Button>
              <Button data-testid="close-unrealized-submit" type="submit" disabled={busy} className="bg-red-700 hover:bg-red-600 text-white font-bold uppercase">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Encerrar"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

function BudgetHistory({ order }) {
  const hist = (order.budget_history || []).filter((b) => b.approval);
  if (!hist.length) return null;
  return (
    <details data-testid="budget-history" className="mt-4 rounded-xl border border-zinc-800 bg-zinc-950/40 p-3">
      <summary className="cursor-pointer text-sm font-semibold text-zinc-300">Aprovações anteriores ({hist.length})</summary>
      <div className="mt-2 space-y-3">
        {hist.slice().reverse().map((b, i) => (
          <div key={b.approval.signature_id || i} data-testid={`budget-history-item-${i}`} className="text-sm text-zinc-400">
            <div>Orçamento anterior: <span className="text-zinc-100 font-bold">{brl(b.total)}</span></div>
            <ApprovalInfo orderId={order.id} approval={b.approval} testId={`budget-history-approval-${i}`} />
          </div>
        ))}
      </div>
    </details>
  );
}

function BudgetTable({ items, total, subtotal, totalDiscount, showMargin }) {
  const costMap = useProductCosts();
  const { user } = useAuth();
  const thresholds = user?.shop?.profit_thresholds || { good: 30, warn: 15 };
  const isP = (i) => !!(i.product_id || i.customer_supplied || i.kind === "p");
  const prods = items.filter(isP);
  const servs = items.filter((i) => !isP(i));
  const sumPaid = (list) => list.reduce((s, i) => s + (Number(i.quantity) || 0) * (Number(i.unit_price) || 0) - (Number(i.discount) || 0), 0);
  const grossProd = prods.reduce((s, i) => s + (Number(i.quantity) || 0) * (Number(i.unit_price) || 0), 0);
  const grossServ = servs.reduce((s, i) => s + (Number(i.quantity) || 0) * (Number(i.unit_price) || 0), 0);
  const discountTotal = items.reduce((s, i) => s + (Number(i.discount) || 0), 0);
  const section = (label, rows, testid) => {
    if (!rows.length) return null;
    const sub = sumPaid(rows);
    const isProduct = testid === "budget-section-parts";
    return (
      <div data-testid={testid} className="rounded-lg border border-zinc-800 overflow-hidden mb-3">
        <div className="px-3 py-1.5 bg-zinc-900 text-[11px] font-mono-caps text-amber-brand uppercase flex items-center justify-between"><span>{label}</span><span className="text-zinc-500">{rows.length} {rows.length === 1 ? "item" : "itens"}</span></div>
        <ul className="sm:hidden divide-y divide-zinc-800">
          {rows.map((it, i) => {
            const line = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0) - (Number(it.discount) || 0);
            const inc = !it.customer_supplied && !isProduct && line === 0;
            return (
              <li key={i} data-testid={`${testid}-item-${i}`} className="p-3 bg-zinc-950/60">
                <div className="text-sm text-zinc-100 font-medium break-words whitespace-normal">{it.code ? <span className="text-zinc-500 text-[11px] font-mono mr-1">{it.code}</span> : null}{it.description}{it.customer_supplied && <span className="ml-1 text-[10px] font-bold text-amber-brand uppercase">(fornecido pelo cliente)</span>}</div>
                {it.explanation && <p className="mt-1 text-xs text-zinc-400 leading-relaxed">{it.explanation}</p>}
                <div className="mt-1.5 text-[12px] text-zinc-400 flex items-center justify-between gap-2 flex-wrap">
                  <span>{it.quantity} × {brl(it.unit_price)}{Number(it.discount) > 0 && <span className="ml-1 text-amber-300">− {brl(it.discount)}</span>}</span>
                  <span className="text-zinc-100 font-bold">{inc ? <span className="text-green-neon">Incluso</span> : `= ${brl(line)}`}</span>
                </div>
                {showMargin && isProduct && <div className="mt-1 text-right"><ItemMarginChip testid={`${testid}-margin-${i}`} item={it} costMap={costMap} thresholds={thresholds} /></div>}
              </li>
            );
          })}
        </ul>
        <table className="hidden sm:table w-full text-sm">
          <thead>
            <tr className="bg-zinc-900 text-zinc-500 text-[11px] font-mono-caps">
              <th className="text-left px-3 py-2 w-20">Cód.</th>
              <th className="text-left px-3 py-2">Descrição</th>
              <th className="text-right px-3 py-2 w-16">Qtd</th>
              <th className="text-right px-3 py-2 w-28">Vl. Unitário</th>
              <th className="text-right px-3 py-2 w-28">Vl. Desconto</th>
              <th className="text-right px-3 py-2 w-28">Vl. Total</th>
              {showMargin && isProduct && <th className="text-right px-3 py-2 w-16">Margem</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((it, i) => {
              const line = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0) - (Number(it.discount) || 0);
              const inc = !it.customer_supplied && !isProduct && line === 0;
              return (
                <tr key={i} className="border-t border-zinc-800">
                  <td className="px-3 py-2 text-zinc-500 font-mono text-xs whitespace-nowrap">{it.code || "—"}</td>
                  <td className="px-3 py-2 text-zinc-200 whitespace-normal break-words">{it.description}{it.customer_supplied && <span className="ml-1 text-[10px] font-bold text-amber-brand uppercase">(fornecido pelo cliente)</span>}{it.explanation && <div className="mt-1 text-xs text-zinc-400 leading-relaxed">{it.explanation}</div>}</td>
                  <td className="px-3 py-2 text-right text-zinc-300">{it.quantity}</td>
                  <td className="px-3 py-2 text-right text-zinc-300 whitespace-nowrap">{brl(it.unit_price)}</td>
                  <td className="px-3 py-2 text-right text-zinc-400 whitespace-nowrap">{brl(it.discount || 0)}</td>
                  <td className="px-3 py-2 text-right text-zinc-100 font-medium whitespace-nowrap">{inc ? <span className="text-green-neon text-xs font-bold uppercase">Incluso</span> : brl(line)}</td>
                  {showMargin && isProduct && <td className="px-3 py-2 text-right whitespace-nowrap"><ItemMarginChip testid={`${testid}-margin-${i}`} item={it} costMap={costMap} thresholds={thresholds} /></td>}
                </tr>
              );
            })}
          </tbody>
        </table>
        <div data-testid={`${testid}-subtotal`} className="flex items-center justify-between gap-3 bg-zinc-900/60 border-t border-zinc-800 px-3 py-2 text-sm">
          <span className="text-[11px] font-mono-caps text-zinc-500">Subtotal</span>
          <span className="font-display font-bold text-zinc-100 tabular-nums">{brl(sub)}</span>
        </div>
      </div>
    );
  };
  return (
    <div data-testid="budget-table">
      {section("Produtos", prods, "budget-section-parts")}
      {section("Serviços", servs, "budget-section-labor")}
      <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3 space-y-1 text-sm" data-testid="budget-table-totals">
        <div className="flex justify-between text-zinc-400"><span>TOTAL PRODUTOS</span><span data-testid="budget-total-parts" className="tabular-nums">{brl(grossProd)}</span></div>
        <div className="flex justify-between text-zinc-400"><span>TOTAL SERVIÇOS</span><span data-testid="budget-total-labor" className="tabular-nums">{brl(grossServ)}</span></div>
        <div className="flex justify-between text-zinc-400"><span>DESCONTOS POR ITEM</span><span data-testid="budget-total-discounts" className="tabular-nums">{discountTotal > 0 ? "− " : ""}{brl(discountTotal)}</span></div>
        {Number(totalDiscount) > 0 && (
          <>
            <div className="flex justify-between text-zinc-400"><span>SUBTOTAL</span><span data-testid="budget-view-subtotal" className="tabular-nums">{brl(subtotal ?? (grossProd + grossServ - discountTotal))}</span></div>
            <div className="flex justify-between text-amber-200"><span>DESCONTO NO TOTAL</span><span data-testid="budget-view-total-discount" className="tabular-nums">− {brl(totalDiscount)}</span></div>
          </>
        )}
        <div className="flex justify-between items-baseline pt-1 border-t border-zinc-800">
          <span className="font-mono-caps text-[11px] text-zinc-400">TOTAL GERAL</span>
          <span data-testid="budget-total-value" className="text-amber-brand font-display text-xl sm:text-2xl font-black whitespace-nowrap tabular-nums">{brl(total)}</span>
        </div>
      </div>
    </div>
  );
}

function DecisionBadge({ decision }) {
  const map = {
    pendente: { label: "Aguardando cliente", cls: "bg-amber-950/60 text-amber-300 border-amber-700" },
    aprovado: { label: "Aprovado", cls: "bg-green-900/50 text-green-300 border-green-brand" },
    recusado: { label: "Recusado", cls: "bg-red-950/40 text-red-300 border-red-900" },
  };
  const m = map[decision] || map.pendente;
  return (
    <span data-testid={`budget-decision-${decision}`} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-bold uppercase ${m.cls}`}>
      {m.label}
    </span>
  );
}
